import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";

const LIKERT_LABELS = [
  { value: 1, label: "Nunca" },
  { value: 2, label: "Raramente" },
  { value: 3, label: "Às vezes" },
  { value: 4, label: "Frequentemente" },
  { value: 5, label: "Sempre" },
  { value: 0, label: "Não se aplica" },
];

/**
 * Printable blank questionnaire — supports two layouts so the same screen
 * serves both compact paper review (tabela densa com colunas 1..5) and reading-
 * friendly handouts where the respondent sees the full text of every option:
 *
 *  - "table"  → original layout: Pergunta + 6 colunas (1..5 + NA), denso
 *  - "labeled" → cada pergunta seguida da lista vertical de respostas escritas
 *               (Nunca / Raramente / Às vezes / Frequentemente / Sempre / NA),
 *               cada uma com checkbox próprio — ideal para entrevistas presenciais.
 */
export default function PrintQuestionnaire() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [testMeta, setTestMeta] = useState(null);
  // Layout can be pre-selected by the caller via ?layout=table|labeled — keeps
  // the in-page toggle for users who landed here directly, but lets the two
  // buttons in AssessmentDetail open the desired mode straight away.
  const initialLayout = params.get("layout") === "labeled" ? "labeled" : "table";
  const [layout, setLayout] = useState(initialLayout);

  useEffect(() => {
    let mounted = true;
    // Step 1: load the assessment so we know which instrument it uses.
    // Step 2: fetch the correct question bank for that test_type. Legacy
    // NR-01 (54q) hits the original /api/questions endpoint untouched; the
    // three new instruments (rapido/copsoq/hse) use /api/questions/{type}
    // which the dispatcher converts into the same legacy shape.
    api.get(`/assessments/${id}`).then(async (a) => {
      if (!mounted) return;
      setData(a.data);
      const testType = a.data?.test_type || "nr01_54";
      const endpoint = testType === "nr01_54" ? "/questions" : `/questions/${testType}`;
      const q = await api.get(endpoint);
      if (!mounted) return;
      setQuestions(q.data.questions);
      setTestMeta(q.data.test_meta || null);
    });
    return () => { mounted = false; };
  }, [id]);

  const grouped = useMemo(() => {
    const map = {};
    questions.forEach((q) => {
      if (!map[q.dimensao]) map[q.dimensao] = [];
      map[q.dimensao].push(q);
    });
    return Object.entries(map);
  }, [questions]);

  if (!data) return <div className="p-12 text-slate-500">Carregando…</div>;

  const c = data.company_snapshot || {};

  return (
    <div className="bg-white min-h-screen">
      <div className="no-print sticky top-0 z-50 bg-slate-900 text-white px-6 py-3 flex justify-between items-center gap-3 flex-wrap">
        <Link to={`/app/avaliacoes/${id}`} className="text-sm" data-testid="print-q-back">← Voltar</Link>
        <div className="text-sm">Questionário para preenchimento manual</div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="inline-flex border border-slate-700 p-0.5 rounded-md bg-slate-800"
            data-testid="print-q-layout-toggle"
          >
            <button
              onClick={() => setLayout("table")}
              data-testid="print-q-layout-table"
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${
                layout === "table" ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"
              }`}
            >
              <i className="fa-solid fa-table-list mr-1"></i> Tabela (1–5)
            </button>
            <button
              onClick={() => setLayout("labeled")}
              data-testid="print-q-layout-labeled"
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${
                layout === "labeled" ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"
              }`}
            >
              <i className="fa-solid fa-list-ul mr-1"></i> Respostas por escrito
            </button>
          </div>
          <button onClick={() => window.print()} className="btn btn-primary text-sm" data-testid="print-q-button">
            <i className="fa-solid fa-print"></i> Imprimir
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-10 py-8 print:py-0">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 pb-4 border-b-2 border-slate-900">
          <img src={LOGO_URL} alt="Sollute" className="h-16 w-auto" />
          <div className="text-right text-xs font-mono text-slate-600">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Avaliação ID</div>
            <div className="text-sm font-bold text-slate-900">{data.id.slice(0, 8).toUpperCase()}</div>
            <div className="mt-1">Token: {data.public_token.slice(0, 12)}…</div>
          </div>
        </div>

        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 mb-1">
          {testMeta ? `Questionário Psicossocial — ${testMeta.short || testMeta.name}` : "Questionário Psicossocial NR-01"}
        </h1>
        <p className="text-sm text-slate-600 mb-1">
          Empresa: <strong>{c.razao_social}</strong> · GHE/Setor: <strong>{data.ghe_setor || "—"}</strong>
        </p>
        {testMeta && testMeta.question_count && (
          <p className="text-xs text-slate-500 mb-4" data-testid="print-q-instrument">
            Instrumento: <strong>{testMeta.name}</strong> · {testMeta.question_count} perguntas · {testMeta.dimensions_count} dimensões
          </p>
        )}
        {!testMeta && <div className="mb-4" />}

        <div className="grid grid-cols-2 gap-4 text-sm mb-5 border border-slate-300 p-3">
          <Field label="Nome (opcional)" />
          <Field label="Matrícula (opcional)" />
          <Field label="Função" />
          <Field label="Data" />
        </div>

        <div className="bg-slate-100 border border-slate-300 p-3 text-xs text-slate-700 mb-4">
          <strong className="block mb-1">Instruções:</strong>
          {layout === "table" ? (
            <>
              Para cada pergunta abaixo, marque <strong>uma</strong> alternativa que melhor descreva sua percepção sobre o trabalho.
              Escala: <strong>1=Nunca · 2=Raramente · 3=Às vezes · 4=Frequentemente · 5=Sempre · NA=Não se aplica</strong>.
              Suas respostas são <strong>anônimas</strong> e serão utilizadas apenas para fins de gestão coletiva.
            </>
          ) : (
            <>
              Para cada pergunta abaixo, marque <strong>uma única</strong> alternativa que melhor descreva a sua
              percepção sobre o trabalho. Se a pergunta não se aplica à sua função, marque <em>Não se aplica</em>.
              Suas respostas são <strong>anônimas</strong> e serão utilizadas apenas para fins de gestão coletiva.
            </>
          )}
        </div>

        {/* Questions */}
        {layout === "table"
          ? <TableLayout grouped={grouped} />
          : <LabeledLayout grouped={grouped} />
        }

        {/* Signature */}
        <div className="mt-10 grid grid-cols-2 gap-12 text-xs text-slate-700 print:break-inside-avoid">
          <div>
            <div className="border-t border-slate-700 pt-1 text-center">Assinatura do respondente (opcional)</div>
          </div>
          <div>
            <div className="border-t border-slate-700 pt-1 text-center">Data de preenchimento</div>
          </div>
        </div>

        <div className="text-center text-[10px] text-slate-400 mt-6">
          Sollute NR-01 · Documento gerado em {new Date().toLocaleDateString("pt-BR")} · ID {data.id.slice(0, 8).toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function Field({ label }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="h-6 border-b border-slate-400"></div>
    </div>
  );
}

function TableLayout({ grouped }) {
  return (
    <>
      {grouped.map(([dim, qs]) => (
        <section key={dim} className="mb-4 print:break-inside-avoid" data-testid={`print-q-dim-${dim}`}>
          <h2 className="font-display text-sm font-extrabold text-emerald-900 uppercase tracking-wider mt-4 mb-2 border-b border-emerald-900 pb-1">
            {dim}
          </h2>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500">
                <th className="text-left font-semibold pb-1 w-6">#</th>
                <th className="text-left font-semibold pb-1">Pergunta</th>
                <th className="text-center font-semibold pb-1 w-6">1</th>
                <th className="text-center font-semibold pb-1 w-6">2</th>
                <th className="text-center font-semibold pb-1 w-6">3</th>
                <th className="text-center font-semibold pb-1 w-6">4</th>
                <th className="text-center font-semibold pb-1 w-6">5</th>
                <th className="text-center font-semibold pb-1 w-7">NA</th>
              </tr>
            </thead>
            <tbody>
              {qs.map((q) => (
                <tr key={q.id} className="border-b border-slate-200">
                  <td className="py-1.5 font-mono text-slate-500 align-top">{q.numero}</td>
                  <td className="py-1.5 pr-2 align-top">{q.pergunta}</td>
                  {[1, 2, 3, 4, 5, "NA"].map((v) => (
                    <td key={v} className="text-center align-middle py-1.5">
                      <span className="inline-block w-4 h-4 border-2 border-slate-700 align-middle"></span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </>
  );
}

function LabeledLayout({ grouped }) {
  return (
    <>
      {grouped.map(([dim, qs]) => (
        <section key={dim} className="mb-6" data-testid={`print-q-dim-${dim}`}>
          <h2 className="font-display text-sm font-extrabold text-emerald-900 uppercase tracking-wider mt-5 mb-3 border-b border-emerald-900 pb-1">
            {dim}
          </h2>
          <div className="space-y-4">
            {qs.map((q) => (
              <article
                key={q.id}
                className="print:break-inside-avoid"
                data-testid={`print-q-item-${q.numero}`}
              >
                <div className="flex items-start gap-2 text-[13px]">
                  <span className="font-mono text-slate-500 shrink-0 w-6 text-right">{q.numero}.</span>
                  <span className="text-slate-900 leading-snug">{q.pergunta}</span>
                </div>
                <ul className="mt-2 ml-8 grid grid-cols-2 gap-x-6 gap-y-1">
                  {LIKERT_LABELS.map((opt) => (
                    <li
                      key={opt.value}
                      className="flex items-center gap-2 text-[12px] text-slate-700"
                    >
                      <span className="inline-block w-3.5 h-3.5 border-2 border-slate-700 shrink-0"></span>
                      <span>{opt.label}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}
